import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { TotalSummarySearchSelector } from './reducers/TotalSummarySearchReducer';
import { setUserDisplayNames, userSelectorSelector } from '../UserSelector/reducers/UserSelectorReducer';
import UserDisplayNameModel from '../UserSelector/models/UserDisplayNameModel';
import UserApi from '../UserManagement/api/UserApi';
import { TotalSummary } from './models/TotalSummaryResult';
import { Months } from '../Expense/ExpenseInput';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';
import { isInRole } from '../MenuBar/MenuBar';

const TotalSummaryTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    const TotalSummarySearchData = useSelector(TotalSummarySearchSelector);
    const userSelectorData = useSelector(userSelectorSelector);

    useEffect(() => {
        if (userSelectorData.userDisplayNames.length === 0) {
            UserApi.GetDisplayNames()
                .then((response: BaseResponse<UserDisplayNameModel[]>) => {
                    if (response.success) {
                        dispatch(setUserDisplayNames(response.data))
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
        }
    }, [])

    let renderTable = (totalSummary: TotalSummary, tableTitle: string, hasBankReceived: boolean) => {
        let excludedUsername = ["Admin", "Finance - Ascent Bulk", "Care - Ascent Bulk"];
        let users = _.filter(userSelectorData.userDisplayNames, (user) => {
            return _.indexOf(excludedUsername, user.displayName) == -1 && 
                isInRole(user.userGroup,[UserGroupEnum.Administrator, UserGroupEnum.Broker, UserGroupEnum.TraineeBroker]);
        })

        let totalSummaryItems = _.filter(totalSummary.summaryItems, (item) => { return item.userDisplayName == "Total" });
        let sumOfTotal = _.reduce(totalSummaryItems, function (sum, item) {
            return sum + item.value;
        }, 0);

        let vnBankReceivedSummaryItems = _.filter(totalSummary.summaryItems, (item) => { return item.userDisplayName == "VN Bank Received" });
        let sumOfVnBank = _.reduce(vnBankReceivedSummaryItems, function (sum, item) {
            return sum + item.value;
        }, 0);

        let sgpBankReceivedSummaryItems = _.filter(totalSummary.summaryItems, (item) => { return item.userDisplayName == "SGP Bank Received" });
        let sumOfSgpBank = _.reduce(sgpBankReceivedSummaryItems, function (sum, item) {
            return sum + item.value;
        }, 0);

        return <>
            <Table className='datatable TotalSummary-datatable-search-result cash-table'>
                <TableHead>
                    <TableRow className='datatable-head'>
                        <TableCell colSpan={users.length + 2}>
                            {tableTitle}
                        </TableCell>
                        {hasBankReceived &&
                            <>
                                <TableCell colSpan={2}>
                                    Bank Received
                                </TableCell>
                            </>
                        }
                    </TableRow>
                    <TableRow>
                        <TableCell>Month</TableCell>
                        {
                            _.map(users, (user) => {
                                return <TableCell>{user.displayName}</TableCell>
                            })
                        }
                        <TableCell>Total</TableCell>
                        {hasBankReceived &&
                            <>
                                <TableCell>VN Bank Received</TableCell>
                                <TableCell>SGP Bank Received</TableCell>
                            </>
                        }
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        _.map(Months, (month) => {
                            let totalSummaryItem = _.find(totalSummary.summaryItems, (item) => { return item.userDisplayName == "Total" && item.month == month });
                            let vnBankReceivedSummaryItem = _.find(totalSummary.summaryItems, (item) => { return item.userDisplayName == "VN Bank Received" && item.month == month });
                            let sgpBankReceivedSummaryItem = _.find(totalSummary.summaryItems, (item) => { return item.userDisplayName == "SGP Bank Received" && item.month == month });
                            return <TableRow>
                                <TableCell>{month}</TableCell>
                                {
                                    _.map(users, (user) => {
                                        let summaryItem = _.find(totalSummary.summaryItems, (item) => { return item.userId == user.id && item.month == month });
                                        return <TableCell>{summaryItem == null ? "" : summaryItem.value == 0 ? "" : Utility.NumberFormat.formatUSD(summaryItem.value)}</TableCell>;
                                    })
                                }
                                <TableCell>{totalSummaryItem == null ? "" : Utility.NumberFormat.formatUSD(totalSummaryItem.value)}</TableCell>
                                {
                                    hasBankReceived &&
                                    <>
                                        <TableCell>{vnBankReceivedSummaryItem == null ? "" : Utility.NumberFormat.formatUSD(vnBankReceivedSummaryItem.value)}</TableCell>
                                        <TableCell>{sgpBankReceivedSummaryItem == null ? "" : Utility.NumberFormat.formatUSD(sgpBankReceivedSummaryItem.value)}</TableCell>
                                    </>
                                }
                            </TableRow>
                        })
                    }
                    <TableRow>
                        <TableCell>Total</TableCell>
                        {
                            _.map(users, (user) => {
                                let summaryItems = _.filter(totalSummary.summaryItems, (item) => {
                                    return item.userId == user.id
                                });

                                let total = _.reduce(summaryItems, function (sum, item) {
                                    return sum + item.value;
                                }, 0);

                                return <TableCell>{Utility.NumberFormat.formatUSD(total)}</TableCell>;
                            })
                        }
                        <TableCell>{Utility.NumberFormat.formatUSD(sumOfTotal)}</TableCell>
                        {
                            hasBankReceived &&
                            <>
                                <TableCell>{Utility.NumberFormat.formatUSD(sumOfVnBank)}</TableCell>
                                <TableCell>{Utility.NumberFormat.formatUSD(sumOfSgpBank)}</TableCell>
                            </>
                        }
                    </TableRow>
                </TableBody>
            </Table>
        </>;
    }

    return <>
        {
            TotalSummarySearchData.totalSummaryResults && userSelectorData.userDisplayNames.length > 0 &&
            <>
                {renderTable(TotalSummarySearchData.totalSummaryResults.totalCommReceived, "Total Comm Received", true)}
                <br />
                {renderTable(TotalSummarySearchData.totalSummaryResults.totalInvoicedComm, "Total Invoiced Comm", false)}
                <br />
                {renderTable(TotalSummarySearchData.totalSummaryResults.estimatedComm, "Estimated Comm", false)}
            </>
        }
    </>
};

export default TotalSummaryTable;